import { computed, onBeforeUnmount, onMounted, watch, type ComputedRef } from 'vue'

/**
 * Browser chrome (iOS Safari's status bar and toolbar, Chrome Android's address
 * bar) is tinted from the document canvas or `<meta name="theme-color">`, never
 * from a `position: fixed` layer. So a PageBackground color is also pushed onto
 * `body` — which propagates to the canvas while `html` has no background, and
 * then paints nothing itself, leaving the fixed layer visible — and the meta.
 *
 * The latest mounted claim wins, so a page transition hands over correctly in
 * whichever order the old unmount and the new mount run.
 */
const claims: ComputedRef<string | undefined>[] = []

function sync() {
  const color = [...claims].reverse().find((claim) => claim.value)?.value
  document.body.style.backgroundColor = color ?? ''

  let meta = document.head.querySelector<HTMLMetaElement>('meta[data-page-background]')
  if (!color) return meta?.remove()
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    meta.dataset.pageBackground = ''
    // Browsers honour the first theme-color in tree order.
    document.head.prepend(meta)
  }
  // theme-color does not resolve `var(--…)`; the computed body color is plain rgb().
  meta.content = getComputedStyle(document.body).backgroundColor
}

export function useDocumentBackground(color: () => string | undefined) {
  const claim = computed(color)
  onMounted(() => {
    claims.push(claim)
    sync()
  })
  watch(claim, sync)
  onBeforeUnmount(() => {
    claims.splice(claims.indexOf(claim), 1)
    sync()
  })
}
